<template>
  <section :id="anchor_id || null" class="gallery">
    <div v-if="images.length">
      <div v-b-modal="modal_id" class="gallery__main">
        <template v-for="(image, image_index) in images">
          <div
            v-if="image_index < 6"
            :key="`image:${_uid}:${image_index}`"
            :class="`gallery__item gallery__item--${image_index + 1}`"
            :style="`background-image:url(${image_url(image)})`"
          ></div>
        </template>
        <div v-b-modal="modal_id" class="gallery__show">
          <vb-icon
            class="gallery__show-icon"
            :name="'gallery'"
            :color="sass('dark')"
            :size="14"
          />
          <span class="gallery__show-text">Vedi tutte le foto</span>
        </div>
      </div>
      <vb-modal
        :vb_options="{
          id: modal_id,
          modal_class: 'gallery__modal',
          dialog_class: images.length > 20 ? 'fullwidth' : '',
          title: name,
          size: 'xl',
          centered: true,
          hide_footer: true,
          ignore_enforce_focus_selector: '.pswp',
        }"
      >
        <div ref="swiper-container" class="gallery__modal-wrap">
          <div
            v-for="(image, image_index) in images"
            :key="_uid + image_index"
            class="gallery__modal-item"
          >
            <a
              class="gallery__modal-anchor"
              :href="image_large(image)"
              :data-pswp-width="get_pswp_size(image_index, 'width')"
              :data-pswp-height="get_pswp_size(image_index, 'height')"
              :class="{
                'gallery__modal-anchor--active':
                  image_index === current_slide_index,
              }"
            >
              <img
                :ref="`img_ref_${image_index}`"
                class="gallery__modal-img"
                :src="image_large(image)"
                alt=""
                @load="get_image_size(image_index)"
              />
            </a>
          </div>
        </div>
      </vb-modal>
    </div>
  </section>
</template>

<script>
  import PhotoSwipeLightbox from 'photoswipe/dist/photoswipe-lightbox.esm.js';
  import 'photoswipe/dist/photoswipe.css';
  import { image_url, image_large } from './logic';

  export default {
    name: 'gallery',
    inheritAttrs: false,
    props: {
      name: {
        type: String,
        default: () => '',
      },
      anchor_id: {
        type: String,
        default: () => '',
      },
      images: {
        type: Array,
        default: () => [],
      },
    },

    data() {
      return {
        lightbox: null,
        current_slide_index: -1,
        image_sizes: [],
      };
    },

    computed: {
      // più gallery possono convivere nella stessa pagina: id e lightbox vanno isolati per istanza
      modal_id() {
        return `modal_gallery_${this._uid}`;
      },
    },

    mounted() {
      this.$root.$on('bv::modal::shown', this.handle_modal_shown);
      this.$root.$on('bv::modal::hide', this.handle_modal_hide);
    },

    beforeDestroy() {
      this.$root.$off('bv::modal::shown', this.handle_modal_shown);
      this.$root.$off('bv::modal::hide', this.handle_modal_hide);
      this.destroy_lightbox();
    },

    methods: {
      image_url,
      image_large,

      handle_modal_shown(bv_event, modal_id) {
        if (modal_id !== this.modal_id || this.lightbox) return;

        // zoom on click
        this.lightbox = new PhotoSwipeLightbox({
          showHideAnimationType: 'fade',
          gallery: `#${this.modal_id}`,
          children: 'a',
          pswpModule: () => import('photoswipe'),
        });
        this.lightbox.init();
        this.lightbox.on('close', this.handle_lightbox_close);
      },

      handle_modal_hide(bv_event, modal_id) {
        if (modal_id !== this.modal_id) return;
        this.destroy_lightbox();
      },

      handle_lightbox_close() {
        const { pswp } = this.lightbox || {};
        if (!pswp || typeof pswp.currIndex !== 'number') return;

        this.current_slide_index = pswp.currIndex;
        this.$nextTick(this.scroll_current_image_into_view);
      },

      scroll_current_image_into_view() {
        const current_image = this.$refs[`img_ref_${this.current_slide_index}`];
        if (Array.isArray(current_image) && current_image[0]) {
          current_image[0].scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      },

      destroy_lightbox() {
        if (!this.lightbox) return;

        this.lightbox.destroy();
        this.lightbox = null;
        this.current_slide_index = -1;
      },

      get_image_size(image_index) {
        const image_node = this.$refs[`img_ref_${image_index}`][0];

        if (image_node) {
          if (!this.image_sizes.some((size) => size.index === image_index)) {
            // evito la ricorsione infinita
            this.image_sizes.push({
              index: image_index,
              width: image_node.naturalWidth,
              height: image_node.naturalHeight,
            });

            this.image_sizes.sort((a, b) => a.index - b.index);
          }
        }
      },

      get_pswp_size(image_index, sizes) {
        const size = this.image_sizes.find((s) => s.index === image_index);
        return size ? size[sizes] : null;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .gallery {
    margin-top: $grid-gutter-width/2;

    @include media-breakpoint-up(sm) {
      margin-top: $grid-gutter-width;
    }

    &__main {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: 1fr;
      grid-column-gap: 0;
      grid-row-gap: 0;
      position: relative;

      @include media-breakpoint-up(md) {
        grid-template-columns: repeat(5, 1fr);
        grid-template-rows: repeat(2, 1fr);
        grid-column-gap: 10px;
        grid-row-gap: 10px;
      }
    }

    &__item {
      border-radius: 10px;
      overflow: hidden;
      background-repeat: no-repeat;
      background-size: cover;

      &--1 {
        grid-area: 1 / 1 / 3 / 3;
        height: 270px;

        @include media-breakpoint-up(xl) {
          height: 385px;
        }
      }
      &--2 {
        grid-area: 1 / 3 / 2 / 4;
        height: 130px;
        display: none;

        @include media-breakpoint-up(md) {
          display: block;
        }

        @include media-breakpoint-up(xl) {
          height: 187px;
        }
      }
      &--3 {
        grid-area: 1 / 4 / 2 / 5;
        height: 130px;
        display: none;

        @include media-breakpoint-up(md) {
          display: block;
        }

        @include media-breakpoint-up(xl) {
          height: 187px;
        }
      }
      &--4 {
        grid-area: 2 / 4 / 3 / 5;
        height: 130px;
        display: none;

        @include media-breakpoint-up(md) {
          display: block;
        }

        @include media-breakpoint-up(xl) {
          height: 187px;
        }
      }
      &--5 {
        grid-area: 2 / 3 / 3 / 4;
        height: 130px;
        display: none;

        @include media-breakpoint-up(md) {
          display: block;
        }

        @include media-breakpoint-up(xl) {
          height: 187px;
        }
      }
      &--6 {
        grid-area: 1 / 5 / 3 / 6;
        height: 270px;
        display: none;

        @include media-breakpoint-up(md) {
          display: block;
        }

        @include media-breakpoint-up(xl) {
          height: 385px;
        }
      }
    }

    &__show {
      display: flex;
      align-items: center;
      cursor: pointer;
      background-color: $white;
      position: absolute;
      bottom: 15px;
      right: 15px;
      border-radius: 5px;
      border: 1px solid $dark;
      padding: 5px 8px;

      &-icon {
        margin-right: 8px;
      }

      &-text {
        color: $dark;
        font-size: $font-size-md;
        font-weight: $vb-font-weight-semibold;
      }
    }

    &__modal {
      &-wrap {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        grid-gap: 10px;

        @include media-breakpoint-up(lg) {
          grid-template-columns: repeat(6, 1fr);
        }

        @include media-breakpoint-up(xl) {
          grid-template-columns: repeat(8, 1fr);
        }

        @include media-breakpoint-up(xxl) {
          grid-template-columns: repeat(10, 1fr);
        }
      }

      &-item {
        aspect-ratio: 1/1;
      }

      &-anchor {
        display: block;
        width: 100%;
        height: 100%;
        overflow: hidden;
        border-radius: 5px;
        position: relative;

        @keyframes flash {
          0%,
          100% {
            // Colore del bordo durante il lampeggio
            border-color: var(--secondary);
          }
          50% {
            border-color: transparent; // Colore alternativo durante il lampeggio
          }
        }

        &:after {
          $width_abs: 0;
          position: absolute;
          top: $width_abs;
          left: $width_abs;
          right: $width_abs;
          bottom: $width_abs;
          transition: $transition-base;
          z-index: 1;
          border: 5px solid transparent;
          border-radius: 5px;
          box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.2) inset;
        }

        &--active {
          img {
            opacity: 0.4;
            transition: all 1s ease 0s;
            transform: scale(1.1);
          }

          &:after {
            content: '';
            animation: flash 1s infinite;
          }
        }
      }

      &-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.2s ease;

        &:hover {
          opacity: 0.5;
          transform: scale(1.1);
        }
      }
    }
  }
</style>
